import type { Editor } from '@tiptap/core';
import type { BlockContextItem, AutocompleteListItem } from '../../types';
import type { AutocompleteAdapter, AutocompleteOptions, TriggerResult, InsertRange } from '../core/types';
import { parseBlockContextFromText } from '../core/textBlockContext';

/**
 * Adapter for HtmlSourceEditor (Tiptap code block).
 * Inserts Handlebars as plain text (e.g., {{variable}}, {{#if}}{{/if}}).
 */
export class TiptapCodeAdapter implements AutocompleteAdapter {
  readonly id = 'tiptap-code';
  readonly editor: Editor;

  private updateCallbacks: Set<() => void> = new Set();
  private selectionCallbacks: Set<() => void> = new Set();
  private unsubscribers: Array<() => void> = [];

  constructor(editor: Editor) {
    this.editor = editor;
    this.setupListeners();
  }

  private setupListeners(): void {
    const handleUpdate = () => {
      this.updateCallbacks.forEach((cb) => cb());
    };

    const handleSelectionUpdate = () => {
      this.selectionCallbacks.forEach((cb) => cb());
    };

    this.editor.on('update', handleUpdate);
    this.editor.on('selectionUpdate', handleSelectionUpdate);

    this.unsubscribers.push(() => {
      this.editor.off('update', handleUpdate);
      this.editor.off('selectionUpdate', handleSelectionUpdate);
    });
  }

  detectTrigger(): TriggerResult | null {
    const { state } = this.editor;
    const { selection, doc } = state;
    const { from } = selection;

    // Get the current text node context
    const $from = doc.resolve(from);

    // Find the start of the current text block (code block)
    const textStart = $from.start();

    // Get text from block start to cursor (no separators = positions match text length)
    const textBefore = doc.textBetween(textStart, from, '', '');

    // Find the last occurrence of {{ that isn't closed by }}
    // This matches the behavior of Tiptap's Suggestion plugin
    const triggerIdx = textBefore.lastIndexOf('{{');
    if (triggerIdx === -1) {
      return null;
    }

    const afterTrigger = textBefore.slice(triggerIdx + 2);

    // Check if it's closed (contains }})
    if (afterTrigger.includes('}}')) {
      return null;
    }

    const query = afterTrigger;
    const triggerStart = textStart + triggerIdx;

    return {
      query,
      range: {
        from: triggerStart,
        to: from,
      },
      triggerPos: triggerStart,
    };
  }

  getCursorRect(): DOMRect | null {
    const { view, state } = this.editor;
    const { from } = state.selection;

    try {
      const coords = view.coordsAtPos(from);
      return new DOMRect(
        coords.left,
        coords.top,
        0,
        coords.bottom - coords.top
      );
    } catch {
      return null;
    }
  }

  insertItem(
    item: AutocompleteListItem,
    range: InsertRange,
    _options: AutocompleteOptions
  ): void {
    const textToInsert = this.formatItemAsText(item);

    this.editor
      .chain()
      .focus()
      .deleteRange(range)
      .insertContent(textToInsert)
      .run();

    // For blocks, position cursor between start and end tags
    if (item.isBlockType && item.blockType && !item.metadata?.isBlockEnd) {
      const endTagLength = `{{/${item.blockType}}}`.length;
      const currentPos = this.editor.state.selection.from;
      this.editor.commands.setTextSelection(currentPos - endTagLength);
    }
  }

  private formatItemAsText(item: AutocompleteListItem): string {
    // Block end tag
    if (item.metadata?.isBlockEnd) {
      return `{{/${item.metadata.blockType}}} `;
    }

    // Block type (start + end)
    if (item.isBlockType && item.blockType) {
      const variable = item.value ? ` ${item.value}` : '';
      return `{{#${item.blockType}${variable}}}{{/${item.blockType}}}`;
    }

    // Inline tag
    return `{{${item.value}}} `;
  }

  getTextContent(): string {
    return this.editor.state.doc.textContent;
  }

  getCursorPosition(): number {
    return this.editor.state.selection.from;
  }

  getBlockContext(): BlockContextItem[] {
    const { state } = this.editor;
    const { selection, doc } = state;
    const { from } = selection;

    // Get the text offset (not ProseMirror position)
    const $from = doc.resolve(from);
    const textStart = $from.start();
    const textOffset = from - textStart;

    const text = this.getTextContent();
    return parseBlockContextFromText(text, textOffset);
  }

  onUpdate(callback: () => void): () => void {
    this.updateCallbacks.add(callback);
    return () => this.updateCallbacks.delete(callback);
  }

  onSelectionUpdate(callback: () => void): () => void {
    this.selectionCallbacks.add(callback);
    return () => this.selectionCallbacks.delete(callback);
  }

  focus(): void {
    this.editor.commands.focus();
  }

  destroy(): void {
    this.unsubscribers.forEach((unsub) => unsub());
    this.updateCallbacks.clear();
    this.selectionCallbacks.clear();
  }
}
